// Shop sheet (IMPLEMENTATION §2.6 "Sheets and cards", §2.8 DOM-shop-to-canvas handoff, UX §2 Shop sheet /
// Wireframe C). It rises from the top edge of the tab bar, clamp(shop.sheetHsRatio·Hs, sheetMin, sheetMax)
// tall: header (SHOP · cash · ✕) · category chips (jump to their first card; each a ≥ 44 px hit around its
// pill, the row shrinks and ellipsizes them to fit a 360 px screen) · ONE `touch-action: pan-x`
// carousel row of 104×136 cards · hint · the rule footer. A card runs the §2.8 mini-FSM (input/shopCardFsm):
// sideways = the row scrolls, upward past 8 px = the pointer is handed to #input (adoptPointer: drag-out, the
// sheet collapses to its 28 px peek BY TRANSFORM, the card stays mounted); a tap opens the detail pop-over
// whose Place button starts tap-placement (§2.8 step 7).
import type { RefObject } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { adoptedDragActive, adoptPointer } from '../../input/adopt.ts';
import {
  CARD_IDLE,
  type CardState,
  type CardStep,
  cardCancel,
  cardDown,
  cardMove,
  cardUp,
} from '../../input/shopCardFsm.ts';
import { fillCopy } from '../copy.ts';
import { shellCtx } from '../ctx.ts';
import { money } from '../format.ts';
import { panelClip, shopSheetHeight } from '../panels.ts';
import { type ShopCardModel, type ShopCategory, shopModel } from '../shopModel.ts';
import { freshSig, layoutSig, modeSig, panelSig, popoverSig, sheetSig } from '../state.ts';
import { snapshotSig } from '../store.ts';
import { box } from './box.ts';
import { Icon } from './Icon.tsx';

interface CardProps {
  card: ShopCardModel;
  rowRef: RefObject<HTMLDivElement>;
  lockPx: number;
  iconPx: number;
  newTag: string;
  onTap(card: ShopCardModel): void;
}

function ShopCard({ card, rowRef, lockPx, iconPx, newTag, onTap }: CardProps) {
  const st = useRef<CardState>(CARD_IDLE);
  const scroll0 = useRef(0);
  const detach = useRef<(() => void) | null>(null);
  useEffect(() => () => detach.current?.(), []);

  const handle = (step: CardStep, e: PointerEvent): void => {
    st.current = step.state;
    switch (step.action) {
      case 'adopt':
        detach.current?.();
        st.current = CARD_IDLE; // the input root owns the pointer now
        if (card.item !== null && card.placeable) adoptPointer(e, { k: 'shop', item: card.item });
        break;
      case 'scroll': {
        const row = rowRef.current;
        const s = step.state;
        if (row && s.k === 'scroll' && s.mouse)
          row.scrollLeft = scroll0.current - (e.clientX - s.x0);
        break;
      }
      case 'tap':
        detach.current?.();
        onTap(card);
        break;
      case 'reset':
        detach.current?.();
        break;
      case 'none':
        if (step.state.k === 'idle') detach.current?.();
        break;
    }
  };

  const onPointerDown = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse' && e.button !== 0) return; // right / middle click: no tap, no drag
    const step = cardDown(st.current, e.pointerId, e.clientX, e.clientY, e.pointerType === 'mouse');
    if (step.state.k === 'pending' && st.current.k === 'idle') {
      scroll0.current = rowRef.current?.scrollLeft ?? 0;
      const id = e.pointerId;
      const move = (ev: PointerEvent): void => {
        if (ev.pointerId === id)
          handle(cardMove(st.current, id, ev.clientX, ev.clientY, lockPx), ev);
      };
      const up = (ev: PointerEvent): void => {
        if (ev.pointerId === id) handle(cardUp(st.current, id), ev);
      };
      const cancel = (ev: PointerEvent): void => {
        if (ev.pointerId === id) handle(cardCancel(st.current, id), ev);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', cancel);
      detach.current = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', cancel);
        detach.current = null;
      };
    }
    handle(step, e);
  };

  return (
    <div
      class={`shop-card ctl ${card.affordable ? '' : 'poor'} ${card.price === null ? 'soldout' : ''}`}
      data-item={card.item ?? undefined}
      data-expansion={card.expansion ?? undefined}
      data-cat={card.cat}
      data-hit="shop-card"
      onPointerDown={onPointerDown}
    >
      {card.isNew ? (
        <span class="card-new" data-testid="card-new">
          {newTag}
        </span>
      ) : null}
      {/* a card with a sub line ("+2× Lead Chunk") gets a smaller icon so two sub lines fit (playtest) */}
      {card.icon !== null ? (
        <Icon icon={card.icon} px={card.sub !== null ? Math.round(iconPx * 0.75) : iconPx} />
      ) : (
        <Icon frame="ui.build" px={card.sub !== null ? Math.round(iconPx * 0.75) : iconPx} />
      )}
      <span class="card-name">{card.name}</span>
      {card.price !== null ? <span class="card-price">{card.price}</span> : null}
      {card.sub !== null ? <span class="card-sub">{card.sub}</span> : null}
    </div>
  );
}

function Popover({ card }: { card: ShopCardModel }) {
  const ctx = shellCtx();
  const f = ctx.shell.fmt();
  const sc = f.copy.shop;
  const act = (e: MouseEvent): void => {
    ctx.actions.click();
    if (card.expansion !== null) {
      const r = ctx.actions.dispatch({ c: 'expand', id: card.expansion });
      if (!r.ok) {
        const b = (e.currentTarget as HTMLElement).getBoundingClientRect();
        ctx.actions.refuse(r.reason, { x: b.x + b.width / 2, y: b.y });
        return;
      }
      ctx.shell.closePanel();
      return;
    }
    if (card.wired) {
      if (modeSig.value?.mode !== 'build') ctx.actions.toggleBuild();
      ctx.actions.setTool('chute');
      ctx.shell.closePanel();
      return;
    }
    if (card.item !== null && card.placeable) {
      ctx.shell.closePanel();
      ctx.actions.beginTapPlace(card.item);
    }
  };
  const label =
    card.expansion !== null
      ? fillCopy(f.copy.cards.expandBuy ?? '{price}', { price: card.price ?? '' })
      : card.wired
        ? (sc.paintInBuild ?? '')
        : (sc.place ?? '');
  const usable = card.expansion !== null || card.wired || card.placeable;
  return (
    <div class="shop-popover" data-testid="shop-popover">
      <div class="pop-text">
        <b class="pop-name">{card.name}</b>
        {card.price !== null ? <span class="pop-price">{card.price}</span> : null}
        {card.sub !== null ? <span class="pop-sub">{card.sub}</span> : null}
      </div>
      {usable ? (
        <button
          type="button"
          class="btn btn-buy ctl"
          data-hit="pop-place"
          data-testid="pop-place"
          onClick={act}
        >
          {label}
        </button>
      ) : null}
      <button
        type="button"
        class="icon-btn ctl"
        data-hit="pop-close"
        aria-label={sc.close ?? ''}
        onClick={() => {
          popoverSig.value = null;
        }}
      >
        ✕
      </button>
    </div>
  );
}

export function ShopSheet() {
  const l = layoutSig.value;
  const state = sheetSig.value;
  const panel = panelSig.value.k;
  void snapshotSig.value; // prices, reveals and ETAs follow the 10 Hz snapshot
  const rowRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<ShopCategory>('deposits');
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    // after adoption the row must never start a native pan (a pan would pointercancel the drag)
    const block = (e: TouchEvent): void => {
      if (adoptedDragActive()) e.preventDefault();
    };
    row.addEventListener('touchmove', block, { passive: false });
    return () => row.removeEventListener('touchmove', block);
  }, [l !== null]);
  if (!l) return null;
  const ctx = shellCtx();
  const f = ctx.shell.fmt();
  const cfg = ctx.data().ui.shop;
  const hitMin = ctx.data().ui.hit.visualMin;
  const snap = snapshotSig.value;
  const model = shopModel(f, ctx.state(), freshSig.value, snap?.goal?.advice ?? null);
  const h = shopSheetHeight(l.Hs, cfg);
  const dy = state === 'open' ? 0 : state === 'peek' ? h - cfg.peek : h;
  const clip = panelClip(l);
  const pop = popoverSig.value;
  const sc = f.copy.shop;
  const step = cfg.cardW + cfg.cardGap;
  const onScroll = (): void => {
    const row = rowRef.current;
    if (!row) return;
    const idx = Math.round(row.scrollLeft / step);
    let cat = model.cats[0]?.id ?? 'deposits';
    for (const c of model.cats) if (c.first <= idx) cat = c.id;
    if (cat !== active) setActive(cat);
  };
  const jump = (first: number, id: ShopCategory): void => {
    const row = rowRef.current;
    setActive(id);
    if (row) row.scrollLeft = first * step;
  };
  const visible = panel === 'shop' || state !== 'closed';
  return (
    <div class="sheet-clip" style={box(clip)}>
      <div
        class={`shop-sheet sheet-${state} ${visible ? 'ctl' : ''}`}
        data-sheet={state}
        data-testid="shop-sheet"
        aria-hidden={state === 'closed'}
        style={{
          height: `${h}px`,
          transform: `translate3d(0,${dy}px,0)`,
          '--sheet-slide': `${cfg.slideMs}ms`,
        }}
      >
        <div class="sheet-head" style={{ height: `${cfg.header}px` }}>
          {state === 'peek' ? (
            <span class="peek-text">{sc.peekCancel ?? sc.peek ?? ''}</span>
          ) : (
            <>
              <span class="sheet-title">{sc.title ?? 'SHOP'}</span>
              <span class="sheet-cash">
                <Icon frame="ui.coin" px={ctx.data().ui.hud.coinPx} />
                {money(f, ctx.state().wallet.cash)}
              </span>
              <button
                type="button"
                class="icon-btn ctl"
                data-hit="shop-close"
                aria-label={sc.close ?? ''}
                onClick={() => ctx.shell.closePanel()}
              >
                ✕
              </button>
            </>
          )}
        </div>
        <div class="sheet-chips" style={{ height: `${cfg.chips}px` }}>
          {model.cats.map((c) => (
            <button
              key={c.id}
              type="button"
              class={`cat-chip ctl ${active === c.id ? 'on' : ''}`}
              data-cat={c.id}
              data-hit="shop-cat"
              style={{ minWidth: `${hitMin}px` }}
              onClick={() => jump(c.first, c.id)}
            >
              <span class="cat-pill">{c.label}</span>
            </button>
          ))}
        </div>
        <div
          class="shop-row ctl"
          ref={rowRef}
          onScroll={onScroll}
          style={{
            height: `${cfg.cardH}px`,
            gap: `${cfg.cardGap}px`,
            '--card-w': `${cfg.cardW}px`,
            '--card-h': `${cfg.cardH}px`,
          }}
        >
          {model.cards.length === 0 ? (
            <span class="sheet-empty" data-testid="shop-empty">
              {sc.empty ?? ''}
            </span>
          ) : null}
          {model.cards.map((card) => (
            <ShopCard
              key={card.key}
              card={card}
              rowRef={rowRef}
              lockPx={ctx.data().ui.input.shopAxisLockPx}
              iconPx={cfg.iconPx}
              newTag={f.copy.tag.new ?? 'NEW!'}
              onTap={(c) => {
                ctx.actions.click();
                popoverSig.value = c;
              }}
            />
          ))}
        </div>
        <div class="sheet-hint" style={{ height: `${cfg.hint}px` }}>
          {sc.hint ?? ''}
        </div>
        <div class="sheet-footer" style={{ height: `${cfg.footer}px` }} data-testid="rule-footer">
          {f.copy.rule.banner ?? ''}
        </div>
        {pop !== null && state === 'open' ? (
          <Popover card={model.cards.find((c) => c.key === pop.key) ?? pop} />
        ) : null}
      </div>
    </div>
  );
}
